import React from "react";
import { Loader2, Sparkles } from "lucide-react";
import { formatReferralCash, FIRST_DEPOSIT_UNLOCK_SCORE } from "../../utils/referralsHelpers";

const clampProgress = (score, threshold, fallback = 0) => {
  if (!threshold || threshold <= 0) return 0;
  return Math.min(100, Math.max(0, fallback || (score / threshold) * 100));
};

const FirstDepositBonusCard = ({ reward, claiming, onClaim, embedded = false }) => {
  const status = reward?.status || "awaiting_deposit";
  const amount = formatReferralCash(reward?.amount);
  const score = reward?.allTimeScores ?? 0;
  const threshold = reward?.unlockScoreThreshold ?? FIRST_DEPOSIT_UNLOCK_SCORE;
  const canClaim = Boolean(reward?.canClaim);
  const isClaimed = status === "claimed";
  const awaitingDeposit = status === "awaiting_deposit";
  const isPending = status === "pending";
  const showScoreBar = isPending || isClaimed;
  const progress = isClaimed
    ? 100
    : clampProgress(score, threshold, reward?.progressPercent);

  let statusText = "";
  if (awaitingDeposit) statusText = "Make a deposit to unlock";
  else if (isClaimed) statusText = "Claimed";
  else if (canClaim) statusText = "Ready to claim";

  const stripClass = [
    "fd-strip",
    embedded ? "fd-strip-embedded" : "",
    showScoreBar ? "fd-strip-has-score" : "",
    embedded && canClaim ? "fd-strip-embedded-ready" : "",
    embedded && isClaimed ? "fd-strip-embedded-claimed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const accentClass = [
    "fd-strip-accent",
    canClaim ? "fd-strip-accent-ready" : "",
    isClaimed ? "fd-strip-accent-claimed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const fillClass = [
    "fd-strip-score-fill",
    canClaim ? "fd-strip-score-fill-ready" : "",
    isClaimed ? "fd-strip-score-fill-claimed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={stripClass} aria-label="First deposit bonus">
      {embedded ? <span className={accentClass} aria-hidden /> : null}

      <Sparkles
        size={embedded ? 12 : 14}
        className={`fd-strip-icon${isClaimed ? " fd-strip-icon-claimed" : ""}`}
        aria-hidden
      />

      <div className="fd-strip-body">
        <p className={`fd-strip-copy${embedded ? " fd-strip-copy-embedded" : ""}`}>
          <span className="fd-strip-label">Welcome bonus </span>
          <strong
            className={`fd-strip-amount tabular-nums${
              isClaimed ? " fd-strip-amount-claimed" : ""
            }`}
          >
            {amount}
          </strong>
          {statusText ? (
            <span
              className={`fd-strip-meta${
                awaitingDeposit ? " fd-strip-meta-unlock" : ""
              }`}
            >
              {statusText}
            </span>
          ) : null}
        </p>

        {showScoreBar ? (
          <div className="fd-strip-score">
            <span className="fd-strip-score-label tabular-nums">
              Score {Math.min(score, threshold)}/{threshold}
            </span>
            <div
              className="fd-strip-score-track"
              role="progressbar"
              aria-valuenow={Math.min(score, threshold)}
              aria-valuemin={0}
              aria-valuemax={threshold}
              aria-label={`All-time score progress ${Math.min(score, threshold)} of ${threshold}`}
            >
              <span className={fillClass} style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : null}
      </div>

      {isClaimed ? (
        <span className="fd-strip-badge">Done</span>
      ) : canClaim ? (
        <button
          type="button"
          className="fd-strip-claim fd-strip-claim-ready"
          onClick={onClaim}
          disabled={claiming}
        >
          {claiming ? <Loader2 size={12} className="animate-spin" /> : null}
          Claim
        </button>
      ) : null}
    </div>
  );
};

export default FirstDepositBonusCard;
